import { supabase } from '../lib/supabase';

export interface MarketIntelligenceSession {
  id: string;
  user_id: string;
  organization_id?: string;
  title: string;
  created_at: string;
  updated_at: string;
  conversations: MarketIntelligenceConversation[];
}

export interface MarketIntelligenceConversation {
  id: string;
  session_id: string;
  query: string;
  response: string;
  data?: any;
  visualizations?: any[];
  query_type?: string;
  role?: string;
  location?: string;
  timestamp: string;
  created_at: string;
}

export interface ChatSession {
  id: string;
  title: string;
  messages: ChatMessage[];
  lastUpdate: string;
  created_at: string;
}

export interface ChatMessage {
  id: string;
  query: string;
  response: string;
  data?: any;
  visualizations?: any[];
  timestamp: string;
  query_type?: string;
  role?: string;
  location?: string;
}

export interface CreateSessionData {
  title?: string;
}

export interface AddConversationData {
  query: string;
  response: string;
  data?: any;
  visualizations?: any[];
  query_type?: string;
  role?: string;
  location?: string;
}

export class MarketIntelligenceChatsService {
  
  // Helper function to get user's organization ID
  private static async getUserOrganizationId(): Promise<string | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data: member } = await supabase
        .from('organization_members')
        .select('organization_id')
        .eq('user_id', user.id)
        .maybeSingle();

      return member?.organization_id || null;
    } catch (error) {
      console.error('Error getting user organization:', error);
      return null;
    }
  }
  
  // Get all chat sessions for the current user
  static async getChatSessions(): Promise<ChatSession[]> {
    try {
      const { data: sessions, error } = await supabase
        .from('market_intelligence_sessions')
        .select(`
          *,
          conversations:market_intelligence_conversations(*)
        `)
        .order('updated_at', { ascending: false });

      if (error) {
        console.error('Error fetching chat sessions:', error);
        throw error;
      }

      // Convert to ChatSession format
      const chatSessions: ChatSession[] = sessions.map(session => ({
        id: session.id,
        title: session.title,
        lastUpdate: session.updated_at,
        created_at: session.created_at,
        messages: session.conversations.map((conv: any) => ({
          id: conv.id,
          query: conv.query,
          response: conv.response,
          data: conv.data,
          visualizations: conv.visualizations,
          timestamp: conv.timestamp,
          query_type: conv.query_type,
          role: conv.role,
          location: conv.location
        }))
      }));

      return chatSessions;
    } catch (error) {
      console.error('Error in getChatSessions:', error);
      throw error;
    }
  }

  // Create a new chat session
  static async createChatSession(data: CreateSessionData = {}): Promise<ChatSession> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Get user's organization ID
      const organizationId = await this.getUserOrganizationId();

      const { data: session, error } = await supabase
        .from('market_intelligence_sessions')
        .insert([{
          title: data.title || 'New Mami Analysis',
          user_id: user.id,
          organization_id: organizationId
        }])
        .select()
        .single();

      if (error) {
        console.error('Error creating chat session:', error);
        throw error;
      }

      return {
        id: session.id,
        title: session.title,
        messages: [],
        lastUpdate: session.updated_at,
        created_at: session.created_at
      };
    } catch (error) {
      console.error('Error in createChatSession:', error);
      throw error;
    }
  }

  // Add a conversation to an existing session
  static async addConversation(
    sessionId: string, 
    conversationData: AddConversationData
  ): Promise<ChatMessage> {
    try {
      const { data: conversation, error } = await supabase
        .from('market_intelligence_conversations')
        .insert([{
          session_id: sessionId,
          query: conversationData.query,
          response: conversationData.response,
          data: conversationData.data || {},
          visualizations: conversationData.visualizations || [],
          query_type: conversationData.query_type,
          role: conversationData.role,
          location: conversationData.location
        }])
        .select()
        .single();

      if (error) {
        console.error('Error adding conversation:', error);
        throw error;
      }

      // Update session timestamp
      await supabase
        .from('market_intelligence_sessions')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', sessionId);

      return {
        id: conversation.id,
        query: conversation.query,
        response: conversation.response,
        data: conversation.data,
        visualizations: conversation.visualizations,
        timestamp: conversation.timestamp,
        query_type: conversation.query_type,
        role: conversation.role,
        location: conversation.location
      };
    } catch (error) {
      console.error('Error in addConversation:', error);
      throw error;
    }
  }

  // Update session title
  static async updateSessionTitle(sessionId: string, title: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('market_intelligence_sessions')
        .update({ title })
        .eq('id', sessionId);

      if (error) {
        console.error('Error updating session title:', error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Error in updateSessionTitle:', error);
      return false;
    }
  }

  // Delete a chat session (and all its conversations)
  static async deleteChatSession(sessionId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('market_intelligence_sessions')
        .delete()
        .eq('id', sessionId);

      if (error) {
        console.error('Error deleting chat session:', error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Error in deleteChatSession:', error);
      return false;
    }
  }

  // Delete a single conversation
  static async deleteConversation(conversationId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('market_intelligence_conversations')
        .delete()
        .eq('id', conversationId);

      if (error) {
        console.error('Error deleting conversation:', error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Error in deleteConversation:', error);
      return false;
    }
  }

  // Get a specific session with all conversations
  static async getSession(sessionId: string): Promise<ChatSession | null> {
    try {
      const { data: session, error } = await supabase
        .from('market_intelligence_sessions')
        .select(`
          *,
          conversations:market_intelligence_conversations(*)
        `)
        .eq('id', sessionId)
        .single();

      if (error) {
        console.error('Error fetching session:', error);
        return null;
      }

      return {
        id: session.id,
        title: session.title,
        lastUpdate: session.updated_at,
        created_at: session.created_at,
        messages: session.conversations.map((conv: any) => ({
          id: conv.id,
          query: conv.query,
          response: conv.response,
          data: conv.data,
          visualizations: conv.visualizations,
          timestamp: conv.timestamp,
          query_type: conv.query_type,
          role: conv.role,
          location: conv.location
        }))
      };
    } catch (error) {
      console.error('Error in getSession:', error);
      return null;
    }
  }

  // Clear all chat sessions for the current user
  static async clearAllChatSessions(): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('market_intelligence_sessions')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all

      if (error) {
        console.error('Error clearing all chat sessions:', error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Error in clearAllChatSessions:', error);
      return false;
    }
  }
}

export default MarketIntelligenceChatsService; 